/**
 * Nextmine: rotation af de varer, forsiden viser.
 *
 * Forsiden har plads til en håndfuld varer, men marketplacet har flere. Viser
 * vi altid bare de nyeste, ser forsiden ens ud dag efter dag, og de varer der
 * ikke længere er helt nye, kommer aldrig forbi. Derfor roterer vi: de nyeste
 * står altid øverst (overskriften hedder trods alt "Recently listed"), og
 * resten af pladserne skifter med faste mellemrum, så alle varer får deres tur.
 *
 * Udvælgelsen er bevidst deterministisk - samme tidsrum giver samme udvalg -
 * af to grunde: serveren og browseren skal vise det samme ved server-rendering,
 * og en besøgende, der klikker ind på en vare og tilbage igen, skal ikke møde
 * en helt ny forside.
 */

/** Hvor længe det samme udvalg står på forsiden, hvis intet andet er sat. */
export const DEFAULT_ROTATION_INTERVAL_MS = 60 * 60 * 1000;

// Hvilket tidsrum vi er i. Tallet tæller én op for hvert interval, der går.
const currentBucket = (now, intervalMs) => Math.floor(now / Math.max(intervalMs, 1));

/**
 * Vælg de varer, forsiden skal vise lige nu.
 *
 * @param {Array} listings varer i nyeste-først rækkefølge (puljen der roteres i)
 * @param {Object} options
 * @param {number} options.count antal pladser på forsiden
 * @param {number} options.pinNewest hvor mange af de nyeste der altid vises
 * @param {number} options.intervalMs hvor længe det samme udvalg står
 * @param {number} options.now tidsstempel (kan sættes i test)
 * @returns {Array} de udvalgte varer, nyeste først i den fastlåste del
 */
export const rotateListings = (listings = [], options = {}) => {
  const {
    count = listings.length,
    pinNewest = 0,
    intervalMs = DEFAULT_ROTATION_INTERVAL_MS,
    now = Date.now(),
  } = options;

  if (count <= 0) {
    return [];
  }
  // Er der ikke flere varer, end der er pladser, er der intet at rotere i.
  if (listings.length <= count) {
    return listings;
  }

  const pinned = listings.slice(0, Math.min(Math.max(pinNewest, 0), count));
  const rest = listings.slice(pinned.length);
  const slots = count - pinned.length;

  if (slots <= 0) {
    return pinned;
  }
  if (rest.length <= slots) {
    return [...pinned, ...rest];
  }

  // Vinduet rykker `slots` frem for hvert interval og løber rundt om puljen,
  // så hver vare før eller siden har været på forsiden.
  const bucket = currentBucket(now, intervalMs);
  const offset = (((bucket * slots) % rest.length) + rest.length) % rest.length;
  const rotated = Array.from({ length: slots }, (_, i) => rest[(offset + i) % rest.length]);

  return [...pinned, ...rotated];
};
